import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import { useT } from '@/lib/i18n';
import { apiFetch } from '@/lib/api';
import { ancoraDaTela, type Ajuda } from '@/lib/ajuda';

/**
 * O "?" do cabeçalho: leva da tela aberta à seção da ajuda que a ensina.
 *
 * A ligação é a CHAVE do item de menu (nav.numbers), e não o texto dele: o painel fala
 * seis idiomas e o manual, por enquanto, um. O mapa vem do servidor já podado pelo
 * papel e pelo trilho — se a pessoa não pode ler aquela seção, o atalho não existe e o
 * botão cai na ajuda geral, em vez de prometer uma página que ela não abriria.
 *
 * Tela sem capítulo escrito também cai na ajuda geral, com o nome dela na busca: é
 * sempre melhor que um "não encontrado".
 */
export function HelpButton({ navKey, titulo }: { navKey?: string; titulo?: string }) {
  const t = useT();
  const { data: manual } = useQuery<Ajuda>({
    queryKey: ['ajuda'],
    queryFn: async () => (await apiFetch('GET', '/help')).data as Ajuda,
    staleTime: 10 * 60 * 1000,
  });

  const destino = ancoraDaTela(navKey ? manual?.telas?.[navKey] : undefined);
  const para = destino
    ? `/ajuda#${destino}`
    : `/ajuda${titulo ? `?q=${encodeURIComponent(titulo)}` : ''}`;

  return (
    <Link
      to={para}
      aria-label={t('ajuda.destaTela')}
      title={t('ajuda.destaTela')}
      className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
    >
      <HelpCircle className="size-[18px]" />
    </Link>
  );
}
